import { PageWrapper, Search } from "@/components";
import { UserInfoWrapper } from "@/components/UserInfo/UserInfoWrapper";
import * as GlobalS from "@/components/styled";
import { CONTENT_WIDTH } from "@/enums";
import { routes } from "@/helpers";
import { useSearchUsersQuery } from "@/services";
import { Pager } from "@/sharedStyled";
import { faEdit } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Table } from "antd";
import { Fragment, useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";

const DEFAULT_PAGE = 1;
const PAGE_SIZE = 12;

const Users = () => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useQueryParams({
    displayName: StringParam,
    email: StringParam,
    department: StringParam,
    jobTitle: StringParam,

    query: StringParam,
    page: NumberParam,
  });

  const {
    data: { users: searchResults, totalCount } = {},
    isFetching: fetchingSearchResults,
    isError,
  } = useSearchUsersQuery(queryParams);

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isError, queryParams]);

  const columns = [
    {
      title: intl.formatMessage({ id: "users.colName" }),
      render: (_, user) => (
        <UserInfoWrapper user={user}>{user.displayName}</UserInfoWrapper>
      ),
    },
    {
      title: intl.formatMessage({ id: "users.colEmail" }),
      dataIndex: "email",
    },
    {
      title: intl.formatMessage({ id: "users.colDepartment" }),
      dataIndex: "department",
    },
    {
      title: intl.formatMessage({ id: "users.colJobTitle" }),
      dataIndex: "jobTitle",
    },
    {
      title: intl.formatMessage({ id: "general.actions" }),
      key: "Actions",
      render: ({ id }) => (
        <Link to={routes.admin.users.get(id)}>
          <Button
            data-test-id={`edit-user-${id}`}
            type="link"
            icon={<FontAwesomeIcon icon={faEdit} />}
          />
        </Link>
      ),
    },
  ];

  const clearAll = useCallback(() => {
    setQueryParams({}, "push");
  }, [setQueryParams]);

  return (
    <Fragment>
      <PageWrapper pageTitle={intl.formatMessage({ id: "users.title" })}>
        <Row className="pb-2">
          <Col
            xxl={CONTENT_WIDTH.oneThird}
            lg={CONTENT_WIDTH.half}
            md={CONTENT_WIDTH.twoThirds}
            xxs={CONTENT_WIDTH.max}
          >
            <Search
              queryParams={queryParams}
              setQueryParams={setQueryParams}
              loadingSearchResults={fetchingSearchResults}
              clearAll={clearAll}
              searchTooltip={intl.formatMessage({
                id: "users.searchTooltip",
              })}
              searchPlaceholder={intl.formatMessage({
                id: "general.searchPlaceholder",
              })}
              parentComponentName="users"
            />
          </Col>
        </Row>
        <Row>
          <Col span={CONTENT_WIDTH.max}>
            <GlobalS.PageContentCard title="Users">
              <Table
                columns={columns}
                dataSource={searchResults}
                rowKey={({ id }) => id}
                pagination={false}
                loading={fetchingSearchResults}
                scroll={{ x: true }}
              />
              <Pager
                defaultCurrent={queryParams.page || DEFAULT_PAGE}
                current={queryParams.page || DEFAULT_PAGE}
                pageSize={PAGE_SIZE}
                total={totalCount}
                showSizeChanger={false}
                onChange={(page) => setQueryParams({ page })}
                data-test-id="users-table-pagination"
              />
            </GlobalS.PageContentCard>
          </Col>
        </Row>
      </PageWrapper>
    </Fragment>
  );
};

export default Users;
